"use client";

import { useState, useEffect, useRef, useId } from "react";
import { Cookie, ExternalLink } from "lucide-react";
import { centralLegalUrl } from "@janeway/app-sdk/public-env";
import { useIsEmbedded } from "../hooks/useIsEmbedded";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DISMISS_KEY = "janeway-cookie-notice-dismissed";

/** Abstand der Karte zum unteren Rand — dieselben 16 px (1rem) wie im Markup;
 *  der Geraeterand (`env(safe-area-inset-bottom)`) kommt dort wie hier dazu. */
const ABSTAND_UNTEN_PX = 16;

/**
 * Marke einer Seite, deren Hauptaktion am unteren Rand steht (Anmeldung,
 * Onboarding). Dort nimmt der Hinweis auch am Desktop die kompakte Zeile: die
 * volle Fassung lag bei 1440x900 ueber „Weiter" (Audit 2026-10-06).
 */
export const KEKS_KOMPAKT_SEITE = "data-jw-keks-kompakt-seite";

function useKompakteSeite(aktiv: boolean): boolean {
  const [kompakt, setKompakt] = useState(false);
  useEffect(() => {
    if (!aktiv) return;
    const pruefe = () => setKompakt(document.querySelector(`[${KEKS_KOMPAKT_SEITE}]`) !== null);
    pruefe();
    const beobachter = new MutationObserver(pruefe);
    beobachter.observe(document.body, { childList: true, subtree: true });
    return () => beobachter.disconnect();
  }, [aktiv]);
  return kompakt;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  // Im iframe einer anderen Oekosystem-App gehoert die Huelle dem Embedder --
  // dieses Chrome-Element wuerde dort nur stoeren (siehe useIsEmbedded).
  const embedded = useIsEmbedded();
  const bannerRef = useRef<HTMLDivElement>(null);
  const vollRef = useRef<HTMLDivElement>(null);
  const vollId = useId();
  // Nur am Handy wirksam: dort steht sonst die kompakte Zeile (MK-13).
  const [ausfuehrlich, setAusfuehrlich] = useState(false);
  const kompakteSeite = useKompakteSeite(visible && !embedded);

  useEffect(() => {
    if (ausfuehrlich) vollRef.current?.focus();
  }, [ausfuehrlich]);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY)) return;
    const timer = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  // **Dieser Hinweis fasst das Dokument nicht an.** Vom 2026-09-19 bis zum 2026-09-20
  // reservierte er seine gemessene Hoehe als `padding-bottom` am `<body>` (F-01: auf
  // dem scrollenden Messenger-Onboarding verdeckte er 30 der 36 Knopf-Pixel). Das war
  // in genau EINER Huelle richtig -- der scrollenden. In 17 der 21 Huellen ist `<body>`
  // `h-[100dvh] overflow-hidden`, also genau EINEN Viewport hoch; dort schiebt
  // Polsterung nichts weg, sie SCHRUMPFT die Huelle (`border-box`): am 2026-09-20 an
  // tasks und drive gemessen endete sie bei y=546 statt 757. Betreiber am selben Tag:
  // „Ich moechte gar nicht, dass es irgendwie die Groesse von den eigentlichen
  // Bildschirmen manipuliert." Also kein `padding`, keine Hoehenrechnung, nichts an
  // `html` oder `body` -- die Karte schwebt darueber.
  // Waechter: `CookieConsent.platzreservierung.test.tsx`.
  //
  // Was bleibt, ist die Bekanntgabe fuer die Nachbarn: `PwaInstallBanner` sitzt auf
  // demselben Fleck (`bottom-4`, auf dem Handy zentriert und gleich breit) und setzt
  // sich mit `calc(1rem + var(--jw-cookieleiste-h, 0px))` darueber statt dahinter.
  // Gemeldet wird der ganze belegte Streifen ab Viewport-Unterkante, also Kartenhoehe
  // PLUS ihr eigener Abstand -- ohne den Aufschlag stuenden beide Karten auf Kante.
  useEffect(() => {
    if (!visible || embedded) return;
    const el = bannerRef.current;
    if (!el) return;
    const melden = () => {
      document.documentElement.style.setProperty(
        "--jw-cookieleiste-h",
        `calc(${el.offsetHeight + ABSTAND_UNTEN_PX}px + env(safe-area-inset-bottom, 0px))`,
      );
    };
    const zuruecknehmen = () => {
      document.documentElement.style.removeProperty("--jw-cookieleiste-h");
    };
    melden();
    // `ResizeObserver` fehlt in aelteren Testumgebungen (jsdom ohne Stub) --
    // die einmalige Meldung oben greift dann trotzdem, nur ohne Nachfuehren
    // bei spaeterem Zeilenumbruch (Drehung, Zoom).
    if (typeof ResizeObserver !== "function") return zuruecknehmen;
    const beobachter = new ResizeObserver(melden);
    beobachter.observe(el);
    return () => {
      beobachter.disconnect();
      zuruecknehmen();
    };
  }, [visible, embedded]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  if (embedded) return null;

  if (!visible) return null;

  return (
    // Eine schwebende Karte unten links, keine randlose Leiste: sie liegt UEBER der
    // Huelle, statt ihr Platz abzuziehen. Position aussen (statisches -translate-x fuer
    // die Zentrierung auf dem Handy), Animation auf dem inneren Wrapper -> kollidiert
    // nicht mit der translateX-Zentrierung.
    // `z-[110]` und nicht mehr `z-[90]`: `PwaInstallBanner` (z-[100]) und die
    // Sonner-Meldungen liegen ebenfalls unten und teilen sich auf dem Handy dieselbe
    // Spalte. Verdeckt werden darf der einzige Knopf nicht, mit dem man den Hinweis
    // wieder loswird. Unter den modalen Schichten (ab `z-[150]`) bleibt er.
    /* `left` haengt an der Leistenbreite, nicht an einer festen Zahl: unten
       links sitzt seit Shell 2.0 der PROFILKNOPF der Seitenleiste, und die
       Karte lag darueber (am 2026-09-20 an `hub` gemessen, Ueberdeckung
       bestaetigt). „Unten links" bleibt richtig — gemeint ist unten links im
       INHALT, nicht im Fenster. `--jw-leiste-ist` kommt von der Leiste selbst
       und ist 0 px, wo es keine gibt; am Handy aendert sich damit nichts. */
    /* MK-13 (`docs/audits/2026-10-03-maps-konkurrenzvergleich.md`): am Handy belegte
       die volle Fassung bei 390x844 rund 233 px und lag ueber Bottom-Sheets und
       Kartenknoepfen. Unter `sm` steht deshalb eine kompakte Zeile mit derselben
       Wahl; „Details" oeffnet die volle Fassung. 1rem plus Geraeterand: die
       Huelle polstert ihre Wurzel, ein `fixed`-Element erreicht das nicht. */
    <div
      ref={bannerRef}
      className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom,0px))] left-1/2 -translate-x-1/2 sm:translate-x-0 z-[110] w-[calc(100%-2rem)] sm:w-auto sm:max-w-sm sm:left-[calc(var(--jw-leiste-ist,0px)+1rem)]"
      role="status"
      aria-label="Cookie-Hinweis"
    >
      <div className="janeway-banner-in rounded-xl border border-border bg-card shadow-lg">
      <div
        data-jw-keks-kompakt=""
        className={
          ausfuehrlich
            ? "hidden"
            : kompakteSeite
              ? "flex items-center gap-2.5 px-3 py-2"
              : "flex sm:hidden items-center gap-2.5 px-3 py-2"
        }
      >
        <Cookie size={16} className="shrink-0 text-primary" />
        <p className="flex-1 min-w-0 text-xs leading-snug text-muted-foreground">
          Nur technisch notwendige Cookies, kein Tracking.{" "}
          <button
            type="button"
            onClick={() => setAusfuehrlich(true)}
            aria-expanded={false}
            aria-controls={vollId}
            className="text-primary underline underline-offset-2"
          >
            Details
          </button>
        </p>
        <button
          onClick={handleDismiss}
          className="shrink-0 px-3 py-2 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          aria-label="Verstanden, Cookie-Hinweis schließen"
        >
          Verstanden
        </button>
      </div>
      <div
        ref={vollRef}
        id={vollId}
        data-jw-keks-voll=""
        tabIndex={-1}
        className={
          ausfuehrlich
            ? "flex flex-col gap-3 px-5 py-4 outline-none"
            : kompakteSeite
              ? "hidden flex-col gap-3 px-5 py-4 outline-none"
              : "hidden sm:flex flex-col gap-3 px-5 py-4 outline-none"
        }
      >
      <div className="flex items-start gap-3">
        <Cookie size={18} className="shrink-0 mt-0.5 text-primary" />
        {/* Der Banner darf kein Cookie behaupten, das es nicht gibt: der
            Empfehlungscode reicht ueber die Ziel-URL (?ref=) durch und liegt in
            sessionStorage — janeway-app-sdk/src/affiliate/route-handlers.ts
            setzt kein Set-Cookie. Aussage bewusst auf die Apps begrenzt, weil
            janeway.io nach Einwilligung sehr wohl _ga setzt. */}
        <p className="text-sm text-muted-foreground leading-relaxed">
          Diese Anwendung verwendet technisch notwendige Cookies für
          Authentifizierung und Einstellungen. Ein Tracking- oder Werbe-Cookie
          setzen wir in den Apps nicht, auch nicht, wenn Sie uns über einen
          Partner-/Empfehlungslink aufrufen. Einzelheiten in der{" "}
          <a
            href={centralLegalUrl("cookies")}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2"
          >
            Cookie-Richtlinie
          </a>
          .
        </p>
      </div>
      <div className="flex items-center justify-between">
        <a
          href={centralLegalUrl("datenschutz")}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary underline underline-offset-2"
        >
          Datenschutz lesen
          <ExternalLink size={11} strokeWidth={2.25} />
        </a>
        <button
          onClick={handleDismiss}
          className="px-4 py-1.5 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
          aria-label="Verstanden, Cookie-Hinweis schließen"
        >
          Verstanden
        </button>
      </div>
      </div>
      </div>
    </div>
  );
}
